import React from 'react';
import AnimateHeight from 'react-animate-height';
import PaddingBox, { Side } from '@uds/padding-box';
import Glyph, { IconName } from '@uds/glyphs';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import { Colorway } from '@uds/theme-utils';

import useStylesheet, { getClassNames } from './ItemFeatures.stylesheet';
import ItemFeature, { FeatureProps } from './ItemFeature';

interface OuterProps {
  colorway: Colorway;
  features: Array<FeatureProps>;
  visibleFeatureLimit: number;
  maxFeatureLimit: number;
  id: string;
  index: number;
  hasMoreFeatures: boolean;
  showMoreFeatures: boolean;
  isEndorsed: boolean;
  onShowMoreClick: (evt: any) => void;
}

export interface InnerProps extends OuterProps {
  toggleHeight: string | number;
  toggleIcon: 'string';
}

const isLast = (index: number, arr: Array<any>) => index === arr.length - 1;
// we have no hidden features, and item is last in array
const isLastVisibleItem = (f: boolean, v: Array<any>, i: number) => !f && isLast(i, v);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const i: number;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const feature: FeatureProps;

const PureSalesboardItemFeatures = ({
  colorway,
  hasMoreFeatures,
  onShowMoreClick,
  isEndorsed,
  showMoreFeatures,
  visibleFeatureLimit,
  features,
  maxFeatureLimit,
  index,
  id,
}: OuterProps) => {
  // pad the array so it is the same length as the longest product
  const parsedFeatures = [...features];
  const toAddNumber = maxFeatureLimit - features.length;
  if (toAddNumber > 0) {
    for (let i = 0; i < toAddNumber; i += 1) {
      parsedFeatures.push({
        text: '\u2014',
      });
    }
  }
  // split into 2 arrays of visible items and hidden items
  let splitFeatures = [parsedFeatures.slice(0, visibleFeatureLimit), parsedFeatures.slice(visibleFeatureLimit)];
  if (isNilOrEmpty(visibleFeatureLimit)) {
    splitFeatures = splitFeatures.reverse();
  }
  const visibleFeatures = splitFeatures[0].map((value, i) => ({
    value,
    index: `vf_${i}_${id}_${index}`,
  }));
  const moreFeatures = splitFeatures[1].map((value, i) => ({
    value,
    index: `mf${i}_${id}_${index}`,
  }));
  // if showMoreFeatures toggle has been activated,
  // or we have a `null` visibleFeatureLimit
  // we want to open up the animated height area
  const openFeatures = showMoreFeatures || visibleFeatureLimit === null;

  const { toggleHeight, toggleIcon } = (() => {
    if (openFeatures) {
      return {
        toggleHeight: 'auto' as string | number,
        toggleIcon: 'chevronUp' as IconName,
      };
    }
    return {
      toggleHeight: 0,
      toggleIcon: 'chevronDown' as IconName,
    };
  })();

  const { styles } = useStylesheet({
    toggleHeight,
    hasMoreFeatures,
  });
  const classNames = getClassNames({ isEndorsed, colorway });
  return (
    <>
      <PaddingBox className={classNames.root}>
        <ul style={styles.list}>
          {visibleFeatures.map((feature, i) => (
            <ItemFeature
              key={feature.index}
              feature={feature.value}
              first={i === 0}
              last={isLastVisibleItem(hasMoreFeatures, visibleFeatures, i)}
            />
          ))}
          <AnimateHeight height={toggleHeight} duration={300}>
            {moreFeatures.map((feature) => (
              <ItemFeature key={feature.index} feature={feature.value} />
            ))}
          </AnimateHeight>
        </ul>
      </PaddingBox>
      {hasMoreFeatures && (
        <PaddingBox side={Side.X} className={classNames.toggleBox}>
          <button type="button" onClick={onShowMoreClick} style={styles.toggle}>
            <Glyph
              name={toggleIcon}
              size={styles.toggleIcon.width as number}
              color={styles.toggleIcon.color as string}
            />
          </button>
        </PaddingBox>
      )}
    </>
  );
};

PureSalesboardItemFeatures.displayName = 'SalesboardItemFeatures';

export default PureSalesboardItemFeatures;
